// Dot grid that the cursor bulges or pushes apart, with an optional glow that follows it.
// Vanilla port of the React Bits DotField: config comes from data-* attributes,
// one instance per `canvas[data-dot-field]`. It pauses off-viewport and on hidden
// tabs, and draws a single still frame under `prefers-reduced-motion`.

const DEFAULTS = {
  spacing: 18,
  radius: 1.2,
  color: "115, 235, 228",
  alpha: 0.16,
  activeAlpha: 0.7,
  influence: 220,
  bulge: 28,
  force: 0.12,
  glowRadius: 200,
  mode: "bulge",
}

function readConfig(canvas) {
  const d = canvas.dataset
  const num = (key) => (d[key] ? parseFloat(d[key]) : DEFAULTS[key])
  return {
    spacing: num("spacing"),
    radius: num("radius"),
    color: d.color || DEFAULTS.color,
    alpha: num("alpha"),
    activeAlpha: num("activeAlpha"),
    influence: num("influence"),
    bulge: num("bulge"),
    force: num("force"),
    glowRadius: num("glowRadius"),
    mode: d.mode || DEFAULTS.mode,
  }
}

function initDotField(canvas) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return

  const cfg = readConfig(canvas)
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const hoverTarget = canvas.closest("aside, section") || canvas.parentElement

  let dots = []
  let width = 0
  let height = 0
  let frame = null
  let visible = false
  const mouse = { x: -9999, y: -9999, inside: false }
  let engagement = 0
  let lastMove = { x: 0, y: 0, t: 0 }

  const build = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    width = canvas.offsetWidth
    height = canvas.offsetHeight
    canvas.width = width * dpr
    canvas.height = height * dpr
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    dots = []
    const offsetX = (width % cfg.spacing) / 2
    const offsetY = (height % cfg.spacing) / 2
    for (let x = offsetX; x <= width; x += cfg.spacing) {
      for (let y = offsetY; y <= height; y += cfg.spacing) {
        dots.push({ hx: x, hy: y, x, y, vx: 0, vy: 0 })
      }
    }
  }

  const draw = () => {
    ctx.clearRect(0, 0, width, height)

    if (cfg.glowRadius > 0 && mouse.inside && engagement > 0.01) {
      const glow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, cfg.glowRadius)
      glow.addColorStop(0, `rgba(${cfg.color}, ${0.12 * engagement})`)
      glow.addColorStop(1, `rgba(${cfg.color}, 0)`)
      ctx.fillStyle = glow
      ctx.fillRect(mouse.x - cfg.glowRadius, mouse.y - cfg.glowRadius, cfg.glowRadius * 2, cfg.glowRadius * 2)
    }

    for (const dot of dots) {
      let proximity = 0
      let nx = 0
      let ny = 0
      if (mouse.inside) {
        const fromX = cfg.mode === "push" ? dot.x : dot.hx
        const fromY = cfg.mode === "push" ? dot.y : dot.hy
        const dx = fromX - mouse.x
        const dy = fromY - mouse.y
        const dist = Math.hypot(dx, dy)
        if (dist < cfg.influence && dist > 0) {
          proximity = 1 - dist / cfg.influence
          nx = dx / dist
          ny = dy / dist
        }
      }

      if (cfg.mode === "push") {
        const kick = proximity * cfg.force * (0.3 + engagement * 0.7) * (cfg.bulge / 20)
        dot.vx = (dot.vx + nx * kick + (dot.hx - dot.x) * 0.02) * 0.9
        dot.vy = (dot.vy + ny * kick + (dot.hy - dot.y) * 0.02) * 0.9
        dot.x += dot.vx
        dot.y += dot.vy
      } else {
        const push = proximity * proximity * cfg.bulge * (0.35 + engagement * 0.65)
        dot.x += (dot.hx + nx * push - dot.x) * cfg.force
        dot.y += (dot.hy + ny * push - dot.y) * cfg.force
      }

      const alpha = cfg.alpha + (cfg.activeAlpha - cfg.alpha) * proximity * proximity
      ctx.fillStyle = `rgba(${cfg.color}, ${alpha})`
      ctx.beginPath()
      ctx.arc(dot.x, dot.y, cfg.radius, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  const loop = () => {
    engagement *= 0.96
    draw()
    frame = visible && !document.hidden ? requestAnimationFrame(loop) : null
  }

  const start = () => {
    if (reducedMotion || frame) return
    frame = requestAnimationFrame(loop)
  }

  const onMove = (event) => {
    const rect = canvas.getBoundingClientRect()
    mouse.x = event.clientX - rect.left
    mouse.y = event.clientY - rect.top
    mouse.inside = true
    const now = performance.now()
    if (now - lastMove.t > 20) {
      const speed = Math.hypot(event.clientX - lastMove.x, event.clientY - lastMove.y) / (now - lastMove.t || 1)
      engagement = Math.min(1, engagement * 0.8 + speed * 0.25)
      lastMove = { x: event.clientX, y: event.clientY, t: now }
    }
    start()
  }

  const onLeave = () => {
    mouse.inside = false
  }

  build()
  draw()

  let resizeTimer = null
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => { build(); draw() }, 100)
  })

  if (reducedMotion) return

  hoverTarget.addEventListener("mousemove", onMove)
  hoverTarget.addEventListener("mouseleave", onLeave)

  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting
    if (visible) start()
  }).observe(canvas)

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && visible) start()
  })
}

export default function setupDotFields() {
  document.querySelectorAll("canvas[data-dot-field]").forEach(initDotField)
}
